import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import './pin-input.css';

class PinInput extends React.Component {
  sanitizeInputProps = () => {
    const { inputProps } = this.props,
      props = { ...(inputProps ? inputProps : {}) };
    // eslint-disable-next-line no-prototype-builtins
    props.hasOwnProperty('ref') && delete props.ref;
    return props;
  };

  saveRef = (index) => {
    return (node) => (this.inputRefs[index] = node);
  };

  setFocus = (value) => {
    const { [Math.min(value.length, this.props.length - 1)]: input } = this.inputRefs;
    input && input.focus();
  };

  onFocus = () => {
    const { value = '' } = this.state;
    this.setFocus(value);
  };

  // handles backspace when the input is empty
  onKeyDown = (e) => {
    const { keyCode } = e;
    const { value = '' } = this.state;

    if (keyCode === 8 && value.length === 0 && this.props.onBackspace) {
      e.preventDefault(); // Prevent default to avoid unwanted behavior
      this.props.onBackspace();
    }
  };

  onKeyUp = async ({ keyCode }) => {
    let { value = '' } = this.state;

    if (keyCode === 8) {
      // Backspace key
      if (value.length > 0) {
        value = value.slice(0, Math.max(0, value.length - 1));
        this.value = value;
        await new Promise((resolve) => this.setState({ value }, resolve));
        this.setFocus(value);
        this.onChange(value);
      }
    } else if (keyCode === 37) {
      // Left arrow
      // allows navigation to the previous input group when at the beginning of the current group
      if (value.length === 0 && this.props.onBackspace) {
        this.props.onBackspace();
      }
    }
  };

  onKeyPress = (index) => {
    return async (e) => {
      const {
        target: { value: key }
      } = e;
      let { value = '' } = this.state;
      if (index === value.length) value += key;
      if (!/^$|^[0-9]+$/g.test(value)) return;
      this.value = value;
      await new Promise((resolve) => this.setState({ value }, resolve));
      this.setFocus(value);
      this.onChange();

      // Check if this group is complete and we should move to next
      if (value.length === this.props.length && this.props.onGroupComplete) {
        this.props.onGroupComplete();
      }
    };
  };

  onPaste = async ({ clipboardData = window.clipboardData }) => {
    const value = (clipboardData.getData('text') || '').slice(0, this.props.length);
    if (value.length <= this.props.length) {
      await new Promise((resolve) => this.setState({ value }, resolve));
      const { onChange } = this.props;
      onChange && onChange(value);

      // Check if pasted value completes this group
      if (value.length === this.props.length && this.props.onGroupComplete) {
        this.props.onGroupComplete();
      }
    }
    this.setFocus(value);
  };

  onChange = () => {
    this.props.onChange?.(this.state.value);
  };

  formatValue = (value) => {
    const { length } = this.props;

    if (value && value.length < length) {
      const emptySpaces = length - value.length;
      return ' '.repeat(emptySpaces) + value;
    }

    return value;
  };

  constructor(props) {
    super(props);
    const initialValue = this.formatValue(props.value || '');
    this.state = {
      value: initialValue,
      showPin: !props.showHideShowButton
    };
    this.inputRefs = {};
    this.value = initialValue;
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      const { value = '' } = this.state;
      this.setFocus(value);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.length !== this.props.length) {
      const redundant = Math.max((this.props.length || 0) - (prevProps.length || 0), 0);
      if (!redundant) return;
      for (let { length } = this.props, i = length - redundant; i < length; i++)
        // eslint-disable-next-line no-prototype-builtins
        this.inputRefs.hasOwnProperty(i) && delete this.inputRefs[i];
    }
    if (prevProps.value !== this.props.value) this.setState({ value: this.props.value });

    // Focus this group if requested
    if (!prevProps.focusGroup && this.props.focusGroup) {
      this.setFocus(this.state.value);
    }
  }

  hideShowPass = () => this.setState({ showPin: !this.state.showPin });

  render() {
    const { length, className, showHideShowButton = false } = this.props,
      { value, showPin } = this.state;
    if (!length) return null;
    return (
      <div className={className ? ['pin-input'].concat(className).join(' ') : 'pin-input'}>
        {Array.from({ length }, (_, i) => {
          return (
            <input
              {...this.sanitizeInputProps()}
              type={showPin ? 'text' : 'password'}
              key={i}
              ref={this.saveRef(i)}
              autoFocus={i === 0 && (this.props.autoFocus || this.props.focusGroup)}
              value={i < value.length ? value[i] : ''}
              onFocus={this.onFocus}
              onKeyDown={this.onKeyDown}
              onKeyUp={this.onKeyUp}
              onChange={this.onKeyPress(i)}
              onPaste={this.onPaste}
              autoComplete="off"
            />
          );
        })}
        {showHideShowButton && (
          <Icon
            name={showPin ? 'hide' : 'unhide'}
            className="hide-show-icon"
            onClick={this.hideShowPass}
          />
        )}
      </div>
    );
  }
}

PinInput.propTypes = {
  length: PropTypes.number,
  className: PropTypes.string,
  inputProps: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
  showHideShowButton: PropTypes.bool,
  onGroupComplete: PropTypes.func,
  focusGroup: PropTypes.bool,
  onBackspace: PropTypes.func,
  page: PropTypes.string,
  autoFocus: PropTypes.bool
};

export default PinInput;
